import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/internal/Observable';
import {
  EmployeeRole,
  PostEmployeeRole,
  PostRole,
  SideMenu,
  UserRole,
  Permission,
  NewPermission,
  PermissionChild,
  PermissionOption,
} from '../../interfaces/role.interface';

@Injectable({
  providedIn: 'root',
})
export class RolesService {
  constructor(private apiService: ApiService, private router: Router) {}

  getAllRoles() {
    return this.apiService.buildAPIGet<UserRole[]>('/users/roles/');
  }

  getRoleById(id: number) {
    return this.apiService.buildAPIGet<UserRole>(`/users/roles/${id}/`);
  }

  postRole(data: PostRole) {
    return this.apiService.buildAPIPost<PostRole>('/users/roles/', data);
  }

  putRole(id: number, data: PostRole) {
    return this.apiService.buildAPIPut<PostRole>(`/users/roles/`, id, data);
  }

  deleteRole(id: number) {
    return this.apiService.buildAPIDelete<void>(`/users/roles/`, id);
  }

  getEmployeeRoles(externalId: string): Observable<EmployeeRole[]> {
    return this.apiService.buildAPIGet<EmployeeRole[]>(
      `/users/employees/${externalId}/roles/`
    );
  }

  getUserSidemenu(externalId: string): Observable<SideMenu[]> {
    return this.apiService.buildAPIGet<SideMenu[]>(
      `/users/menu/?external_id=${externalId}`
    );
  }

  postEmployeeRole(data: PostEmployeeRole): Observable<PostEmployeeRole> {
    return this.apiService.buildAPIPost<PostEmployeeRole>(
      `/users/employee-roles/`,
      data
    );
  }

  deleteEmployeeRole(id: number, externalId: string): Observable<void> {
    //  console.log('Deleting employee role with id:', id);
    return this.apiService.buildAPIDelete<void>(
      `/users/employees/${externalId}/roles/?role_id=${id}`
    );
  }

  /**
   * Type guard to check if permission uses new structure
   */
  private isNewPermission(permission: any): permission is NewPermission {
    return permission && 'identifier' in permission && 'options' in permission;
  }

  /**
   * Type guard to check if permission uses old structure
   */
  private isLegacyPermission(permission: any): permission is Permission {
    return (
      permission && 'functionality' in permission && 'can_read' in permission
    );
  }

  /**
   * Returns the permissions object for the given functionality for the current user.
   * Aggregates permissions across all user roles using OR logic.
   * @param functionality The identifier of the functionality (e.g., 'dining_room', 'communication', etc.) or legacy name
   * @param storageService Pass a StorageService instance
   * @param childIdentifier Optional child identifier for granular permissions (e.g., 'general', 'prices')
   */
  getPermissionsForFunctionality(
    functionality: string,
    storageService: { getItem: <T>(key: string) => T | null },
    childIdentifier?: string
  ): { can_read: boolean; can_write: boolean; can_delete: boolean } | null {
    const userProfile = storageService.getItem<any>('user_profile');

    // Check if user is admin first
    if (userProfile?.is_admin) {
      return {
        can_read: true,
        can_write: true,
        can_delete: true,
      };
    }

    // Initialize aggregated permissions
    let aggregatedPermissions = {
      can_read: false,
      can_write: false,
      can_delete: false,
    };

    let hasFoundAnyPermission = false;

    // Try to get permissions from the new roles system
    const userRoles = storageService.getItem<EmployeeRole[]>('user_roles');
    if (userRoles && userRoles.length > 0) {
      // Search through all roles to aggregate permissions
      for (const userRole of userRoles) {
        if (userRole.role_details && userRole.role_details.permissions) {
          const permissions = userRole.role_details.permissions;

          if (permissions.length > 0 && this.isNewPermission(permissions[0])) {
            // Handle new permission structure
            const rolePermissions = this.extractPermissionsFromRole(
              permissions,
              functionality,
              childIdentifier
            );

            if (rolePermissions) {
              hasFoundAnyPermission = true;
              // Aggregate using OR logic
              aggregatedPermissions.can_read =
                aggregatedPermissions.can_read || rolePermissions.can_read;
              aggregatedPermissions.can_write =
                aggregatedPermissions.can_write || rolePermissions.can_write;
              aggregatedPermissions.can_delete =
                aggregatedPermissions.can_delete || rolePermissions.can_delete;
            }
          } else {
            // Handle legacy permission structure
            const permission = permissions.find(
              (perm: any) =>
                this.isLegacyPermission(perm) &&
                perm.functionality === functionality
            ) as Permission;

            if (permission) {
              hasFoundAnyPermission = true;
              // Aggregate using OR logic
              aggregatedPermissions.can_read =
                aggregatedPermissions.can_read || permission.can_read;
              aggregatedPermissions.can_write =
                aggregatedPermissions.can_write || permission.can_write;
              aggregatedPermissions.can_delete =
                aggregatedPermissions.can_delete || permission.can_delete;
            }
          }
        }
      }

      // Return aggregated permissions if any were found
      if (hasFoundAnyPermission) {
        return aggregatedPermissions;
      }
    }

    // Fallback to old system for backward compatibility
    if (
      userProfile &&
      userProfile.serveto_user_roles &&
      userProfile.serveto_user_roles.length > 0
    ) {
      const role = userProfile.serveto_user_roles[0];
      if (role && role.permissions && Array.isArray(role.permissions)) {
        const permission = role.permissions.find(
          (perm: any) =>
            this.isLegacyPermission(perm) &&
            perm.functionality === functionality
        ) as Permission;

        if (permission) {
          return {
            can_read: permission.can_read,
            can_write: permission.can_write,
            can_delete: permission.can_delete,
          };
        }
      }
    }

    return null;
  }

  /**
   * Extracts permissions for a specific functionality from a single role's permissions
   */
  private extractPermissionsFromRole(
    permissions: any[],
    functionality: string,
    childIdentifier?: string
  ): { can_read: boolean; can_write: boolean; can_delete: boolean } | null {
    // First, try to find direct match with functionality identifier
    const mainPermission = permissions.find(
      (perm: any) => perm.identifier === functionality
    ) as NewPermission;

    if (mainPermission) {
      // If looking for a specific child permission
      if (childIdentifier && mainPermission.children) {
        const childPermission = mainPermission.children.find(
          (child: PermissionChild) => child.identifier === childIdentifier
        );

        if (childPermission) {
          return this.extractPermissionsFromOptions(childPermission.options);
        }
      } else {
        // Return parent-level permissions
        return this.extractPermissionsFromOptions(mainPermission.options);
      }
    }

    // If no direct match found and no childIdentifier specified,
    // search through all children for the functionality identifier
    if (!childIdentifier) {
      for (const permission of permissions) {
        if (this.isNewPermission(permission)) {
          if (permission.children) {
            const childPermission = permission.children.find(
              (child: PermissionChild) => child.identifier === functionality
            );

            if (childPermission) {
              return this.extractPermissionsFromOptions(
                childPermission.options
              );
            }
          }
        }
      }
    }

    return null;
  }

  /**
   * Extracts permissions from options array
   */
  private extractPermissionsFromOptions(options: PermissionOption[]): {
    can_read: boolean;
    can_write: boolean;
    can_delete: boolean;
  } {
    const writeOption = options.find(
      (opt: PermissionOption) => opt.key === 'write'
    );
    const deleteOption = options.find(
      (opt: PermissionOption) => opt.key === 'delete'
    );

    const canWrite = writeOption?.value || false;
    const canDelete = deleteOption?.value || false;

    return {
      can_read: canWrite || canDelete, // Read is implicit if user has write or delete
      can_write: canWrite,
      can_delete: canDelete,
    };
  }

  /**
   * Checks if user has write permission for a functionality and shows an error if not.
   * Returns true if user has write permission, false otherwise.
   *
   * @param functionality The identifier of the functionality (e.g., 'dining_room', 'communication', etc.) or legacy name
   * @param storageService StorageService instance
   * @param snackBar MatSnackBar instance for showing errors
   * @param router Router for navigation if user lacks permissions
   * @param redirectUrl URL to redirect to if user lacks permissions
   * @param customMessage Optional custom error message
   * @param childIdentifier Optional child identifier for granular permissions
   */
  checkWritePermission(
    functionality: string,
    storageService: { getItem: <T>(key: string) => T | null },
    snackBar: any,
    router: Router,
    redirectUrl: string,
    customMessage?: string,
    childIdentifier?: string
  ): boolean {
    const permissions = this.getPermissionsForFunctionality(
      functionality,
      storageService,
      childIdentifier
    );

    if (!permissions || !permissions.can_write) {
      // const message =
      //   customMessage || `No tienes permisos para añadir en ${functionality}`;
      // snackBar.open(message, 'Cerrar', {
      //   duration: 5000,
      // });

      if (redirectUrl) {
        router.navigate([redirectUrl]);
      }

      return false;
    }

    return true;
  }

  /**
   * Convenience method to get permissions by identifier (for new API structure)
   * @param identifier The functionality identifier (e.g., 'dining_room', 'communication')
   * @param storageService StorageService instance
   * @param childIdentifier Optional child identifier for granular permissions
   */
  getPermissionsByIdentifier(
    identifier: string,
    storageService: { getItem: <T>(key: string) => T | null },
    childIdentifier?: string
  ): { can_read: boolean; can_write: boolean; can_delete: boolean } | null {
    return this.getPermissionsForFunctionality(
      identifier,
      storageService,
      childIdentifier
    );
  }

  /**
   * Check if user has read permission for a specific functionality
   */
  hasReadPermission(
    identifier: string,
    storageService: { getItem: <T>(key: string) => T | null },
    childIdentifier?: string
  ): boolean {
    const permissions = this.getPermissionsByIdentifier(
      identifier,
      storageService,
      childIdentifier
    );
    return permissions?.can_read || false;
  }

  /**
   * Check if user has write permission for a specific functionality
   */
  hasWritePermission(
    identifier: string,
    storageService: { getItem: <T>(key: string) => T | null },
    childIdentifier?: string
  ): boolean {
    const permissions = this.getPermissionsByIdentifier(
      identifier,
      storageService,
      childIdentifier
    );
    return permissions?.can_write || false;
  }

  /**
   * Check if user has delete permission for a specific functionality
   */
  hasDeletePermission(
    identifier: string,
    storageService: { getItem: <T>(key: string) => T | null },
    childIdentifier?: string
  ): boolean {
    const permissions = this.getPermissionsByIdentifier(
      identifier,
      storageService,
      childIdentifier
    );
    return permissions?.can_delete || false;
  }
}
